import React, { FC, useCallback, useContext, useState } from 'react'
import Dialog from '../../../components/dialog/Dialog'
import { Grid } from '@mui/material'
import Button from '../../../components/button/Button'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import CloseIcon from '@mui/icons-material/Close'
import { RaisonRefusEnum } from '../enums/raisonRefusEnum'
import { objectGetValues } from '../../../utils/ieCompatibility'
import RaisonRefusBtn from './RaisonRefusBtn'
import { LoadingState } from '../types/loadingState'
import { Field, Form } from 'react-final-form'
import TextField from '../../../components/fields/TextField'
import { refuserRDV } from '../api/visitesApi'
import links from '../../../enums/linksEnum'
import { DEFAULT_OFFER_FILTERS } from '../../rechercheOffre/utils/rechercheOffreConstants'
import { useNavigate } from 'react-router'
import { SelectionPaneContext } from '../../../components/selectionPane/components/SelectionPaneContext'
import { RefusRDVFormValues, RefusRendezVous } from '../types/refusRDVModel'
import { RefusRDVFormValuesEnum } from '../enums/RefusRDVFormValuesEnum'

const RefusRDV: FC = () => {
	const { classes } = useStyles()
	const navigate = useNavigate()

	const [isOpen, setIsOpen] = useState<boolean>(false)
	const [loadingState, setLoadingState] = useState<LoadingState>({ loading: false })

	const { currentOffer, demandeResidentielId, resetSelection } = useContext(SelectionPaneContext)

	const onSubmit = useCallback((values: RefusRDVFormValues) => {
		const data: RefusRendezVous = {
			demande_residentiel_id: demandeResidentielId,
			offre_residentiel_id: currentOffer?.identifiantCRMOffre,
			...values
		}

		setLoadingState({
			loading: true,
			index: values[RefusRDVFormValuesEnum.RAISON_REFUS]
		})
		refuserRDV(data)
			.finally(() => {
				setLoadingState({ loading: false })
				setIsOpen(false)
				resetSelection()
				navigate({ pathname: links.RECHERCHE_OFFRE, search: DEFAULT_OFFER_FILTERS })
			})
	}, [demandeResidentielId, currentOffer, navigate, resetSelection, setLoadingState])

	return (
		<Grid container item>
			<Button
				background="backgroundWhite"
				color="colorAccessibility"
				textStyle="text4"
				buttonStyle={classes.buttonStyle}
				onClick={() => setIsOpen(true)}
				fullWidth
			>
				<FormattedMessage
					id="visites.popin.refusRDV.bouton"
					defaultMessage="Refuser le rendez-vous"
					description="Bouton pour refuser le rdv"
				/>
			</Button>
			<Dialog
				onCloseDialog={() => setIsOpen(false)}
				open={isOpen}
				fullScreen={false}
				maxWidth="lg"
			>
				<Grid container item padding="20px">
					<CloseIcon
						onClick={() => setIsOpen(false)}
						className={classes.iconStyle}
					/>
					<Form
						onSubmit={onSubmit}
						mutators={{
							setRaisonRefus: ([value], state, { changeValue }) => {
								changeValue(state, RefusRDVFormValuesEnum.RAISON_REFUS, () => value)
							}
						}}
						render={({ form, handleSubmit }) => (
							<form onSubmit={handleSubmit}>
								<Grid container item flexDirection="column" spacing="15px">
									<Grid container item>
										<Field
											name={RefusRDVFormValuesEnum.COMMENTAIRE}
											component={TextField}
											label="visites.popin.refusRDV.commentaire"
											multiline
											disabled={loadingState.loading}
										/>
										<Field name={RefusRDVFormValuesEnum.RAISON_REFUS} component="input" hidden/>
									</Grid>
									{objectGetValues(RaisonRefusEnum).filter((v) => !isNaN(Number(v))).map((raison) =>
										<RaisonRefusBtn
											key={raison}
											raisonRefus={raison}
											loadingState={loadingState}
											onClickRefus={(raisonRefus) => {
												form.mutators.setRaisonRefus(raisonRefus)
												handleSubmit()
											}}
										/>
									)}
								</Grid>
							</form>
						)}
					/>
				</Grid>
			</Dialog>
		</Grid>
	)
}

const useStyles = makeStyles()((theme) => ({
	iconStyle: {
		'&:hover': {
			background: theme.palette.blue['5']
		},
		position: 'absolute',
		top: 15,
		right: 12,
		borderRadius: '100%',
		color: theme.palette.blue.main,
		cursor: 'pointer',
		zIndex: 2,
		fontSize: 20,
		padding: 5
	},
	buttonStyle: {
		border: `1px solid ${theme.palette.accessibility.main}`,
		borderRadius: 25
	}
}))

export default RefusRDV